import React, { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { shortenUrl, cn } from "@/src/lib/utils";
import { Share2, Copy, Check, Layout, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface ShareModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  originalUrl: string;
  defaultTitle: string;
  defaultDescription: string;
  defaultImage: string;
  shopName?: string;
  productName?: string;
  availableImages?: string[];
}

export function ShareModal({
  isOpen,
  onOpenChange,
  originalUrl,
  defaultTitle,
  defaultDescription,
  defaultImage,
  shopName,
  productName,
  availableImages = []
}: ShareModalProps) {
  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState(defaultDescription);
  const [image, setImage] = useState(defaultImage);
  const [cardType, setCardType] = useState<"summary" | "summary_large_image">("summary_large_image");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [copied, setCopied] = useState(false);

  // Update states when props change (needed because component might persist)
  useEffect(() => {
    if (isOpen) {
      setTitle(defaultTitle);
      setDescription(defaultDescription);
      setImage(defaultImage);
      setGeneratedUrl("");
    }
  }, [isOpen, defaultTitle, defaultDescription, defaultImage]);

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const url = await shortenUrl(
        originalUrl,
        shopName,
        productName,
        image,
        description,
        cardType
      );
      setGeneratedUrl(url);
      toast.success("¡Enlace personalizado generado!");
    } catch (error) {
      toast.error("Error al generar el enlace");
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      toast.success("Enlace copiado al portapapeles");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Error al copiar");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-primary" />
            Personalizar Compartido
          </DialogTitle>
          <DialogDescription>
            Configura cómo se verá el enlace cuando lo compartas en WhatsApp, Facebook o Twitter.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          <div className="grid gap-2">
            <Label htmlFor="share-title">Título de la tarjeta</Label>
            <Input 
              id="share-title" 
              value={title} 
              onChange={(e) => setTitle(e.target.value)} 
              placeholder="Ej: Oferta Increíble en Mi Tienda"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="share-desc">Descripción</Label>
            <Textarea 
              id="share-desc" 
              value={description} 
              onChange={(e) => setDescription(e.target.value)} 
              placeholder="Escribe un mensaje atractivo..."
              className="resize-none"
              rows={3}
            />
          </div>

          <div className="grid gap-2">
            <Label>Imagen de previsualización</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {[defaultImage, ...availableImages.filter(img => img !== defaultImage)].slice(0, 5).map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setImage(img)}
                  className={cn(
                    "relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all",
                    image === img ? "border-primary ring-2 ring-primary/20 scale-105" : "border-transparent opacity-60 hover:opacity-100"
                  )}
                >
                  <img src={img} alt="Preview" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  {image === img && (
                    <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
                      <Check className="h-4 w-4 text-primary bg-white rounded-full p-0.5" />
                    </div>
                  )}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input 
                value={image} 
                onChange={(e) => setImage(e.target.value)} 
                placeholder="O pega una URL de imagen aquí..." 
                className="text-xs"
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Estilo de tarjeta (Tamaño)</Label>
            <RadioGroup 
              value={cardType} 
              onValueChange={(val: any) => setCardType(val)}
              className="grid grid-cols-2 gap-4"
            >
              <div>
                <RadioGroupItem value="summary" id="card-small" className="peer sr-only" />
                <Label
                  htmlFor="card-small"
                  className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                >
                  <Square className="mb-3 h-6 w-6" />
                  <span className="text-xs font-medium">Miniatura (Pequeña)</span>
                </Label>
              </div>
              <div>
                <RadioGroupItem value="summary_large_image" id="card-large" className="peer sr-only" />
                <Label
                  htmlFor="card-large"
                  className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                >
                  <Layout className="mb-3 h-6 w-6" />
                  <span className="text-xs font-medium">Grande (Banner)</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Real-time Preview simulation */}
          <div className="border rounded-xl p-4 bg-slate-50 space-y-2">
            <Label className="text-[10px] uppercase text-muted-foreground font-bold">Vista previa aproximada</Label>
            <div className="bg-white border rounded-lg overflow-hidden shadow-sm max-w-[300px] mx-auto">
              {cardType === "summary_large_image" ? (
                <div className="aspect-[1.91/1] bg-muted relative">
                  <img src={image} className="w-full h-full object-cover" alt="Preview" referrerPolicy="no-referrer" />
                </div>
              ) : null}
              <div className="p-3 flex gap-3">
                {cardType === "summary" && (
                  <div className="w-16 h-16 bg-muted shrink-0 rounded overflow-hidden">
                    <img src={image} className="w-full h-full object-cover" alt="Preview" referrerPolicy="no-referrer" />
                  </div>
                )}
                <div className="space-y-1 overflow-hidden">
                  <p className="text-sm font-bold truncate">{title || "Cargando título..."}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{description || "Sin descripción"}</p>
                  <p className="text-[10px] text-blue-500 truncate">empremarket.com</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {!generatedUrl ? (
            <Button onClick={handleGenerate} className="w-full" disabled={isGenerating}>
              {isGenerating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Generar Enlace Corto
            </Button>
          ) : (
            <div className="flex flex-col w-full gap-3">
              <div className="flex gap-2 w-full">
                <Input value={generatedUrl} readOnly className="font-mono text-xs bg-muted" />
                <Button size="icon" onClick={copyToClipboard} variant={copied ? "default" : "outline"}>
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <Button onClick={() => setGeneratedUrl("")} variant="ghost" size="sm" className="text-xs">
                Generar otro diferente
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
